// Turn whatever a user hands the Add-document zone — loose files, dropped
// folders, a folder picked via webkitdirectory, or .zip archives — into a
// flat list of importable document Files. Folders are walked, zips are
// unpacked in the browser (fflate), and everything is filtered to the
// importable extensions. Junk (`__MACOSX/`, dotfiles, empty files) is dropped.
import { unzipSync } from 'fflate';
import { readDataTransfer } from './folders';

const ZIP_RE = /\.zip$/i;
const JUNK_RE = /(^|\/)(__MACOSX\/|\.[^/]*$|Thumbs\.db$|desktop\.ini$)/i;
const MIME = {
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  pdf: 'application/pdf',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xlsm: 'application/vnd.ms-excel.sheet.macroEnabled.12',
  txt: 'text/plain', rtf: 'application/rtf', md: 'text/markdown', eml: 'message/rfc822',
};

const baseName = (p) => p.split('/').pop();

async function unzipToFiles(zipFile, acceptRe, out, skipped, depth) {
  let entries;
  try {
    entries = unzipSync(new Uint8Array(await zipFile.arrayBuffer()));
  } catch {
    skipped.push(`${zipFile.name} (could not open zip)`);
    return;
  }
  for (const [path, bytes] of Object.entries(entries)) {
    if (path.endsWith('/') || JUNK_RE.test(path) || !bytes.length) continue;
    const name = baseName(path);
    if (ZIP_RE.test(name)) {
      // One level of nesting is plenty; deeper zips are almost always junk.
      if (depth < 1) await unzipToFiles(new File([bytes], name), acceptRe, out, skipped, depth + 1);
      else skipped.push(`${name} (nested zip)`);
      continue;
    }
    if (!acceptRe.test(name)) { skipped.push(name); continue; }
    const ext = name.split('.').pop().toLowerCase();
    out.push(new File([bytes], name, { type: MIME[ext] || '', lastModified: zipFile.lastModified }));
  }
}

// `input` is a FileList / File[] (picker, webkitdirectory) or a DataTransfer
// (drop). Returns { files, skipped } — `skipped` names what was ignored so
// the UI can say so instead of silently dropping half a folder.
export async function expandDocumentFiles(input, acceptRe) {
  const raw = input instanceof DataTransfer
    ? (await readDataTransfer(input)).map((e) => e.file)
    : Array.from(input || []);
  const out = [];
  const skipped = [];
  for (const file of raw) {
    const rel = file.webkitRelativePath || file.name;
    if (JUNK_RE.test(rel) || !file.size) continue;
    if (ZIP_RE.test(file.name)) await unzipToFiles(file, acceptRe, out, skipped, 0); // eslint-disable-line no-await-in-loop
    else if (acceptRe.test(file.name)) out.push(file);
    else skipped.push(file.name);
  }
  // De-duplicate by name+size (the same file dragged twice, or present in
  // both a folder and a zip) — the later copy loses.
  const seen = new Set();
  const files = out.filter((f) => { const k = `${f.name}|${f.size}`; if (seen.has(k)) return false; seen.add(k); return true; });
  return { files, skipped };
}
